import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ReactNode, useState } from 'react';
import { renderSkeletonCards } from '../../../../components/SkeletonElements';
import { useIotsApi } from '../../../../contexts/iotsApi';
import { useLanguage } from '../../../../contexts/language';
import {
  CrewMember,
  CrewMemberCertificate,
  NewCrewMemberCertificate,
} from '../../../../types/crew';
import CertificationCard from './CertificationCard';
import DeleteCertificateConfirmationDialog from '../../../../components/DeleteCertificateConfirmationDialog';
import {
  ListElement,
  UseDraftListResult,
} from '../../../../hooks/useDraftList';
import useApiErrorMessageGetter from '../../../../hooks/useApiErrorMessage';
import AddButtonCard from '../../../../components/AddButtonCard';

export interface CertificationsTabContentProps {
  crewMember: CrewMember;
  draftListManager: UseDraftListResult<CrewMemberCertificate>;
  emptyContent: ReactNode;
  showAddButton?: boolean;
  isFetchingCertificates: boolean;
  areCertificatesPending: boolean;
  onCertificatesDataUpdate: () => void;
}

const CertificationTabContent = ({
  crewMember,
  draftListManager,
  emptyContent,
  showAddButton = false,
  areCertificatesPending,
  isFetchingCertificates,
  onCertificatesDataUpdate,
}: CertificationsTabContentProps) => {
  const { t } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const { addCrewMemberCertificate, deleteCrewMemberCertificate } =
    useIotsApi();

  const [certificateListElementToDelete, setCertificateListElementToDelete] =
    useState<ListElement<CrewMemberCertificate> | null>(null);

  const {
    mutate: applyAddCertificateMutation,
    isPending: isAddCertificatePending,
    reset: resetAddCertificateMutation,
  } = useMutation({
    mutationFn: (newCertificate: NewCrewMemberCertificate) =>
      addCrewMemberCertificate(crewMember.id, newCertificate),
  });

  const {
    mutate: applyDeleteCertificateMutation,
    isPending: isDeleteCertificatePending,
    reset: resetDeleteCertificateMutation,
  } = useMutation({
    mutationFn: (certificateId: string) =>
      deleteCrewMemberCertificate(crewMember.id, certificateId),
  });

  if (areCertificatesPending) {
    return <div className="flex flex-col gap-4">{renderSkeletonCards(3)}</div>;
  }

  const { list, isReady, operations } = draftListManager;

  if (!list.length) {
    return (
      <>
        {emptyContent}
        {showAddButton && (
          <AddButtonCard
            disabled={!isReady}
            onClick={() => operations.addDraftElement()}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {showAddButton && (
        <AddButtonCard
          disabled={!isReady}
          onClick={() => operations.addDraftElement()}
        />
      )}
      {list.map(item =>
        item.isDraft ? (
          <CertificationCard
            crewMember={crewMember}
            certificate={null}
            isLoading={isFetchingCertificates || isAddCertificatePending}
            onDelete={hasPendingChanges => {
              if (hasPendingChanges) {
                setCertificateListElementToDelete(item);
              } else {
                operations.removeElement(item);
              }
            }}
            onSave={newCertificate => {
              applyAddCertificateMutation(newCertificate, {
                onSuccess: certificate => {
                  operations.promoteDraftToPersistentElement(
                    item.id,
                    certificate,
                  );
                  enqueueSnackbar({
                    variant: 'success',
                    message: t('content.certificate-creation-success'),
                  });
                  onCertificatesDataUpdate();
                },
                onError: error => {
                  enqueueSnackbar({
                    variant: 'error',
                    message: getErrorMessage(
                      error,
                      t('content.certificate-creation-error'),
                    ),
                  });
                  resetAddCertificateMutation();
                },
              });
            }}
            key={item.id}
          />
        ) : (
          <CertificationCard
            crewMember={crewMember}
            certificate={item.data}
            isLoading={isFetchingCertificates || isAddCertificatePending}
            onSave={modifiedCertificationValues => {
              // TODO: Implement edition
              console.log(
                'Save changes to certificate',
                modifiedCertificationValues,
              );
            }}
            key={item.data.id}
            onDelete={() => {
              setCertificateListElementToDelete(item);
            }}
            onBaseDataUpdate={updatedData => {
              operations.updatePersistentElement(item.data, updatedData);
              onCertificatesDataUpdate();
            }}
          />
        ),
      )}
      <DeleteCertificateConfirmationDialog
        certificateItem={certificateListElementToDelete}
        isLoading={isDeleteCertificatePending}
        onClose={() => {
          if (!isDeleteCertificatePending) {
            setCertificateListElementToDelete(null);
          }
        }}
        onConfirm={() => {
          if (!certificateListElementToDelete) {
            return;
          }
          if (certificateListElementToDelete.isDraft) {
            operations.removeElement(certificateListElementToDelete);
            setCertificateListElementToDelete(null);
            return;
          }

          const { data: certificate } = certificateListElementToDelete;
          applyDeleteCertificateMutation(certificate.id, {
            onSuccess: () => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.certificate-deletion-success'),
              });
              operations.removeElement(certificateListElementToDelete);
              setCertificateListElementToDelete(null);
              onCertificatesDataUpdate();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.certificate-deletion-error'),
                ),
              });
              resetDeleteCertificateMutation();
            },
          });
        }}
      />
    </div>
  );
};

export default CertificationTabContent;
